import React from 'react';
import { errorMessage, getProjectStatistics } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import type { Project, ProjectStatistics } from '../../types';
import { Button, Card, CardBody, InlineAlert, Table, Td, Th } from '../../ui';
import { Metric, num, pct } from '../reports/metrics';

/**
 * Field progress for the whole campaign (GET /project/{id}/statistics): wanted, completes, what is left, and the
 * incidence / quota-full rates. `source` says where the numbers come from: "redirects" (the survey redirect links:
 * campaign level only) until respondents use the entry link, then "sessions", which adds the respondent figures
 * (in survey, timed out, drop-out and conversion rates, median length) and a line per target group. Per quota row
 * figures are shown on the Quotas step.
 */
export function CampaignProgressCard({ project }: { project: Project }) {
  const stats = useAsyncData<ProjectStatistics | null>((signal) => getProjectStatistics(project.id, { signal }), [project.id]);
  const s = stats.data;
  const progress = s && typeof s.progress === 'number' ? Math.max(0, Math.min(100, s.progress)) : null;
  const sessions = Boolean(s && s.source === 'sessions');

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink">Field progress</h2>
            <p className="mt-1 text-sm text-muted">Completes so far against what this campaign wants, counted from {sessions ? 'respondent sessions (panelists who used the entry link)' : 'the survey redirect links'}.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={stats.reload} loading={stats.loading}>Refresh</Button>
        </div>

        {stats.error ? (
          <div className="mt-4"><InlineAlert tone="warn">Could not load the statistics: {errorMessage(stats.error)}</InlineAlert></div>
        ) : !s && !stats.loading ? (
          <div className="mt-4"><InlineAlert tone="info">Statistics are not available for this campaign yet.</InlineAlert></div>
        ) : s ? (
          <>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-chip" role="progressbar" aria-valuenow={progress ?? 0} aria-valuemin={0} aria-valuemax={100} aria-label="Completes compared with wanted completes">
                <div className="h-full rounded-full bg-brand-600 transition-all" style={{ width: `${progress ?? 0}%` }} />
              </div>
              <span className="w-14 text-right text-sm font-semibold tabular-nums text-ink">{pct(s.progress)}</span>
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
              <Metric label="Wanted completes" value={num(s.wantedCompletes)} hint="Added up from the target groups' basic settings" />
              <Metric label="Completes" value={num(s.complete)} />
              <Metric label="Remaining" value={num(s.remainingCompletes)} />
              <Metric label="Returned in total" value={num(s.total)} hint="Completes, screen-outs and quota-full together" />
              <Metric label="Screen-outs" value={num(s.screenout)} />
              <Metric label="Quota full" value={num(s.quotaFull)} />
              <Metric label="Incidence rate" value={pct(s.incidenceRate)} hint="Complete ÷ (complete + screen-out). Timed-out and in-survey respondents are not counted." />
              <Metric label="Quota-full rate" value={pct(s.quotaFullRate)} hint="Quota full ÷ (complete + screen-out + quota full). Includes people turned away at entry." />
            </dl>
            {sessions && s && (
              <>
                <h3 className="mt-6 text-sm font-semibold text-ink">Respondents</h3>
                <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                  <Metric label="Entries" value={num(s.entries)} hint="Everyone who passed the entry checks and started" />
                  <Metric label="In survey" value={num(s.inSurvey)} hint="Started and not finished yet" />
                  <Metric label="Timed out" value={num(s.timedOut)} hint="No exit within 60 minutes" />
                  <Metric label="Quality terminate" value={num(s.qualityTerminate)} />
                  <Metric label="Possible duplicate" value={num(s.possibleDuplicate)} />
                  <Metric label="Suspicious" value={num(s.suspicious)} hint="Completed in under 60 seconds: worth checking on the Respondents tab" />
                  <Metric label="Drop-out rate" value={pct(s.dropOutRate)} hint="Timed out ÷ (complete + screen-out + timed out). In-survey respondents are not counted." />
                  <Metric label="Conversion rate" value={pct(s.conversionRate)} hint="Complete ÷ entries. Entries are every entry-link arrival except possible duplicates." />
                  <Metric label="Median length" value={typeof s.medianLoiMinutes === 'number' ? `${s.medianLoiMinutes} min` : '—'} hint="Median survey time of the completes, in minutes" />
                </dl>
                {s.targetGroups && s.targetGroups.length > 0 && (
                  <div className="-mx-5 mt-5">
                    <Table caption="Field progress by target group">
                      <thead><tr><Th>Target group</Th><Th numeric>Wanted</Th><Th numeric>Completes</Th><Th numeric>Remaining</Th><Th numeric>In survey</Th><Th numeric>Screen-outs</Th><Th numeric>Quota full</Th><Th numeric>Progress</Th></tr></thead>
                      <tbody>
                        {s.targetGroups.map((g, i) => (
                          <tr key={g.targetGroupId} className="hover:bg-canvas">
                            <Td className="font-semibold">{g.name || `Target group ${i + 1}`}</Td>
                            <Td numeric className="tabular-nums text-muted">{num(g.wantedCompletes)}</Td>
                            <Td numeric className="tabular-nums">{num(g.complete)}</Td>
                            <Td numeric className="tabular-nums text-muted">{num(g.remainingCompletes)}</Td>
                            <Td numeric className="tabular-nums text-muted">{num(g.inSurvey)}</Td>
                            <Td numeric className="tabular-nums text-muted">{num(g.screenout)}</Td>
                            <Td numeric className="tabular-nums text-muted">{num(g.quotaFull)}</Td>
                            <Td numeric className="tabular-nums">{pct(g.progress)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}
